import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useState, useRef, useSyncExternalStore, } from "react";
import { flushSync } from "react-dom";
import { Button } from "catalyst/button";
import { Sidebar, SidebarBody, SidebarFooter, SidebarHeader, } from "catalyst/sidebar";
import { AppLogo } from "./app-logo.js";
import { appSwitchDestination, crossAppHref, finishAppArrival, } from "./app-navigation.js";
import { getTheme, getServerTheme, getResolvedTheme, subscribeTheme, } from "./theme.js";
const names = { hub: "AI Hub", agent: "Железяка" };
const footerClasses = "relative z-40 shrink-0 border-t-0! pt-0.5! pb-3!";
const switchClasses = "w-full min-w-0 justify-start! px-[calc(--spacing(2)-1px)]!";
export function AppSidebar({ app, otherAppOrigin, activeHubId, ready = true, onNavigate, children, footer, }) {
    const otherApp = app === "hub" ? "agent" : "hub";
    useSyncExternalStore(subscribeTheme, getTheme, getServerTheme);
    const switchLabel = `Перейти в ${otherApp === "agent" ? "Железяку" : "AI Hub"}`;
    const href = activeHubId ? crossAppHref(otherAppOrigin, activeHubId) : null;
    const top = useRef(null);
    const incoming = useRef(null);
    const cover = useRef(null);
    const movingLogo = useRef(null);
    const previousLogo = useRef(null);
    const animations = useRef([]);
    const pending = useRef(false);
    const [transition, setTransition] = useState(null);
    function cancel() {
        animations.current.forEach((animation) => animation.cancel());
        animations.current = [];
        pending.current = false;
        const nav = top.current?.closest("[data-app-sidebar]");
        if (nav)
            nav.inert = false;
    }
    function restore() {
        cancel();
        setTransition(null);
    }
    useLayoutEffect(() => {
        if (!ready)
            return;
        let cancelled = false;
        // Headless UI starts its mobile drawer transition over two frames.
        let frame = requestAnimationFrame(() => {
            frame = requestAnimationFrame(async () => {
                const opening = new Set();
                for (const nav of document.querySelectorAll("[data-app-sidebar]")) {
                    if (!nav.getBoundingClientRect().width)
                        continue;
                    for (let element = nav; element; element = element.parentElement)
                        element
                            .getAnimations()
                            .forEach((animation) => opening.add(animation));
                }
                await Promise.allSettled([...opening].map((animation) => animation.finished));
                if (!cancelled)
                    finishAppArrival();
            });
        });
        return () => {
            cancelled = true;
            cancelAnimationFrame(frame);
        };
    }, [ready]);
    useEffect(() => {
        const onPageShow = (event) => {
            if (event.persisted)
                restore();
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape" && pending.current)
                restore();
        };
        window.addEventListener("pageshow", onPageShow);
        window.addEventListener("keydown", onKeyDown);
        return () => {
            window.removeEventListener("pageshow", onPageShow);
            window.removeEventListener("keydown", onKeyDown);
            cancel();
        };
    }, []);
    async function switchApp(event, destination = href, resume = true) {
        if (!destination ||
            !activeHubId ||
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey)
            return;
        event.preventDefault();
        if (pending.current)
            return;
        pending.current = true;
        const nav = top.current?.closest("[data-app-sidebar]");
        const target = appSwitchDestination(app, destination, activeHubId, resume, nav?.querySelector("[data-app-hub-label]")?.textContent ?? undefined);
        const active = top.current?.querySelector("svg");
        const next = incoming.current?.querySelector("svg");
        const slot = incoming.current?.parentElement;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
            !nav?.animate ||
            !active ||
            !next ||
            !slot) {
            window.location.assign(target);
            return;
        }
        const navRect = nav.getBoundingClientRect();
        const currentRect = active.getBoundingClientRect();
        const nextRect = next.getBoundingClientRect();
        const geometry = {
            startX: nextRect.left - navRect.left,
            startY: nextRect.top - navRect.top,
            endX: currentRect.left - navRect.left,
            endY: currentRect.top - navRect.top,
            height: nextRect.height,
            edge: slot.getBoundingClientRect().top - navRect.top,
            scale: currentRect.height / nextRect.height,
            dark: getResolvedTheme() === "dark",
            desktop: window.matchMedia("(min-width: 1024px)").matches,
        };
        // Mount the cover and start its animation in the same frame: no intermediate
        // logo at the wrong position. Menu text stays at its original scale.
        flushSync(() => setTransition(geometry));
        nav.inert = true;
        const timing = {
            duration: 560,
            easing: "cubic-bezier(0.4, 0, 0.2, 1)",
            fill: "forwards",
        };
        const surface = geometry.desktop
            ? nav.parentElement.parentElement
            : nav.parentElement;
        const backgroundColor = getComputedStyle(surface).backgroundColor;
        const targetBackground = getComputedStyle(cover.current).backgroundColor;
        // Start from the mounted styles; define each logo's geometry only once.
        animations.current = [
            cover.current.animate([
                { backgroundColor },
                { clipPath: "inset(0px 0 0 0)", backgroundColor: targetBackground },
            ], timing),
            movingLogo.current.animate([
                {
                    transform: `translate(${geometry.endX}px, ${geometry.endY}px)`,
                    height: `${geometry.height * geometry.scale}px`,
                },
            ], timing),
            previousLogo.current.animate([
                {
                    transform: `translate(${geometry.startX}px, ${geometry.startY}px) scale(1)`,
                },
            ], timing),
            previousLogo.current.animate([
                { opacity: 1 },
                { opacity: 0, offset: 0.35 },
                { opacity: 0, offset: 0.65 },
                { opacity: 1 },
            ], { ...timing, easing: "linear" }),
            nav
                .querySelector("[data-app-sidebar-body]")
                .animate([{ opacity: 0, transform: "translateY(12px)" }], {
                ...timing,
                duration: 160,
                easing: "ease-out",
            }),
        ];
        function morphLogo(element, source, expand) {
            const brand = element.querySelector("[data-app-logo-brand]");
            animations.current.push(brand.animate([
                { opacity: expand ? 0 : 1 },
                { opacity: 0, offset: 0.35 },
                { opacity: expand ? 1 : 0 },
            ], timing));
            for (const part of element.querySelectorAll("[data-app-logo-name], [data-app-logo-word]")) {
                const compact = getComputedStyle(part).transform;
                const frames = [
                    { transform: compact },
                    { transform: "translate(0px, 0px)" },
                ];
                animations.current.push(part.animate(expand ? frames : frames.reverse(), timing));
            }
            const sourcePaths = source.querySelectorAll("path");
            element.querySelectorAll("path").forEach((part, index) => {
                animations.current.push(part.animate([
                    { fill: getComputedStyle(sourcePaths[index]).fill },
                    { fill: getComputedStyle(part).fill },
                ], timing));
            });
        }
        morphLogo(movingLogo.current, next, true);
        morphLogo(previousLogo.current, active, false);
        if (!geometry.desktop) {
            // The mobile close-button row changes theme with the sidebar surface.
            const frame = nav.parentElement;
            const closeIcon = frame?.querySelector('[aria-label="Close navigation"] svg');
            if (frame) {
                animations.current.push(frame.animate([{ backgroundColor }, { backgroundColor: targetBackground }], timing));
            }
            if (closeIcon) {
                const fill = getComputedStyle(closeIcon).fill;
                animations.current.push(closeIcon.animate([
                    { fill },
                    {
                        fill: geometry.dark
                            ? "var(--color-zinc-400)"
                            : "var(--color-zinc-500)",
                    },
                ], timing));
            }
        }
        try {
            await Promise.all(animations.current.map((animation) => animation.finished));
            window.location.assign(target);
            // Keep the final frame while the next document loads. A timeout here
            // would flash the old menu back during a slow navigation.
        }
        catch {
            restore();
        }
    }
    return (_jsxs(Sidebar, { "data-app-sidebar": app, "aria-label": names[app], className: "relative isolate", children: [_jsx(SidebarHeader, { className: "relative z-20 shrink-0", children: _jsx(Button, { plain: true, href: "/", ref: top, className: "w-full justify-start! px-[calc(--spacing(2)-1px)]!", "data-app-logo": app, "data-app-active": "true", "aria-label": `CoreInfra ${names[app]}`, onClick: onNavigate, children: _jsx(AppLogo, { app: app, className: `h-9 max-w-full${transition ? " invisible" : ""}` }) }) }), _jsx(SidebarBody, { "data-app-sidebar-body": true, className: "min-h-0", children: children }), _jsx("div", { className: "flex shrink-0 flex-col border-t border-zinc-950/5 px-4 pt-3 dark:border-white/5", children: _jsx(Button, { plain: true, ...(href ? { href } : {}), disabled: !href, ref: incoming, className: `${switchClasses} group/app-switch`, "data-app-switcher": otherApp, "data-app-logo": otherApp, "data-app-active": "false", "aria-label": switchLabel, title: switchLabel, onClick: switchApp, children: _jsx(AppLogo, { app: otherApp, muted: true, className: `h-6 max-w-full${transition ? " invisible" : ""}` }) }) }), _jsx(SidebarFooter, { className: footerClasses, children: typeof footer === "function"
                    ? footer((event, destination) => void switchApp(event, destination, false))
                    : footer }), transition && (_jsxs("div", { "data-app-transition": true, "aria-hidden": "true", className: "pointer-events-none absolute inset-0 z-30", children: [_jsx("div", { ref: cover, "data-app-transition-cover": true, className: `absolute inset-0 ${transition.dark
                            ? transition.desktop
                                ? "bg-zinc-950"
                                : "bg-zinc-900"
                            : transition.desktop
                                ? "bg-zinc-100"
                                : "bg-white"}`, style: { clipPath: `inset(${transition.edge}px 0 0 0)` } }), _jsx("div", { ref: previousLogo, "data-app-transition-previous-logo": true, className: "absolute top-0 left-0 origin-top-left", style: {
                            height: transition.height,
                            transform: `translate(${transition.endX}px, ${transition.endY}px) scale(${transition.scale})`,
                        }, children: _jsx(AppLogo, { app: app, muted: true, compact: true, theme: transition.dark ? "dark" : "light", className: "h-full w-auto max-w-none" }) }), _jsx("div", { ref: movingLogo, "data-app-transition-logo": true, className: "absolute top-0 left-0 origin-top-left", style: {
                            height: transition.height,
                            transform: `translate(${transition.startX}px, ${transition.startY}px)`,
                        }, children: _jsx(AppLogo, { app: otherApp, compact: true, theme: transition.dark ? "dark" : "light", className: "h-full w-auto max-w-none" }) })] }))] }));
}
